import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';

const GATE_TYPES = new Set(['swing_gate', 'double_leaf_gate', 'sectional_gate', 'roller_gate']);
const SIDE_PAD_Z = 25 / 100; // 0.25 – 25 cm w jednostkach sceny
const ADDONS_SPACING = 10 / 100; // 0.10 - 10 cm w jednostkach sceny

export default function CollisionDetector({
                                            gateRefs,
                                            gatesData = [],
                                            doorRefs,
                                            doorsData,
                                            windowsData,
                                            length,
                                            width
                                          }) {
  const redBoxesGroup = useRef(new THREE.Group());
  const { scene } = useThree();

  useEffect(() => {
    scene.add(redBoxesGroup.current);
    return () => scene.remove(redBoxesGroup.current);
  }, [scene]);

  const padAxis = (box, axis, pad) => {
    if (axis === 'x') {
      box.min.x -= pad; box.max.x += pad;
    } else if (axis === 'y') {
      box.min.y -= pad; box.max.y += pad;
    } else if (axis === 'z') {
      box.min.z -= pad; box.max.z += pad;
    }
  };

  const getWallNormalAxis = (wall) => {
    const w = String(wall || '').toLowerCase();
    const isSideWall = w.includes('left') || w.includes('right');
    // back/front -> Z, left/right -> X
    return isSideWall ? 'x' : 'z';
  };

  const checkCollisions = (boxes, wall) => {
    const colliding = new Set();
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        if (boxes[i].box.intersectsBox(boxes[j].box)) {
          const a = boxes[i];
          const b = boxes[j];
          console.warn(
            `🔴 Collision on "${wall}" between ${a.kind} ${a.refIndex + 1} and ${b.kind} ${b.refIndex + 1}`
          );
          colliding.add(a);
          colliding.add(b);
        }
      }
    }
    return Array.from(colliding);
  };

  const getWorldBox = (item) => {
    if (!item?.closedBoundingBox || !item?.positionAdjusted) return undefined;

    const src = item.closedBoundingBox.box;
    if (!src) return undefined;

    const box = new THREE.Box3(src.min.clone(), src.max.clone());

    const wallNormalAxis = getWallNormalAxis(item.wall);

    // 1) dodatkowy bufor tylko dla bram segmentowych i rolowanych (po osi normalnej do ściany)
    const isGate = GATE_TYPES.has(item?.type);
    const needsPad = isGate && (item.type === 'sectional_gate' || item.type === 'roller_gate');

    if (needsPad) {
      padAxis(box, wallNormalAxis, SIDE_PAD_Z);
    }

    // 2) ADDONS_SPACING dla dodatków (okna + drzwi): Y + oś normalna do ściany
    //    (czyli: back/front => Z+Y, left/right => X+Y)
    const isAddon = !isGate; // w tym komponencie "addon" = drzwi/okno
    if (isAddon) {
      padAxis(box, 'y', ADDONS_SPACING);
      padAxis(box, wallNormalAxis, ADDONS_SPACING);
    }

    return box;
  };

  useFrame(() => {
    const flattenedDoors = Object.entries(doorsData || {}).flatMap(([wall, doors]) =>
      (doors || []).map((door) => ({ ...door, wall }))
    );

    const flattenedWindows = Object.entries(windowsData || {}).flatMap(([wall, windowTypes]) =>
      Object.entries(windowTypes || {})
        .filter(([key]) => key !== 'count')
        .flatMap(([type, windows]) => (windows || []).map((window) => ({ ...window, wall, type })))
    );

    const gatesWithWalls = (gatesData || []).map((g) => ({ ...g, wall: g.wall }));

    const allItems = [...gatesWithWalls, ...flattenedDoors, ...flattenedWindows];

    const boxes = allItems
      .map((item, index) => {
        if (!item.wall) return null;

        const box = getWorldBox(item);
        if (!box) return null;

        let kind = 'door';
        if (index < gatesData.length) kind = 'gate';
        else if (item?.type && !GATE_TYPES.has(item.type)) kind = 'window';

        return { box, refIndex: index, kind, wall: item.wall };
      })
      .filter(Boolean);

    if (boxes.length < 2) return;

    const boxesByWall = {};
    for (const b of boxes) {
      (boxesByWall[b.wall] ||= []).push(b);
    }

    let collidingItems = [];
    for (const wall in boxesByWall) {
      collidingItems.push(...checkCollisions(boxesByWall[wall], wall));
    }

    redBoxesGroup.current.clear();

    collidingItems.forEach(({ box }) => {
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
      const material = new THREE.MeshBasicMaterial({
        color: 'red',
        transparent: true,
        opacity: 0.3,
        depthWrite: false,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(center);
      redBoxesGroup.current.add(mesh);
    });
  });

  return null;
}
